import { authedFetch } from "@/lib/authed-fetch";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Loader2,
  Folder,
  FileIcon,
  Download,
  ChevronRight,
  AlertTriangle,
  FolderOpen,
} from "lucide-react";

interface DropboxEntry {
  type: "file" | "folder";
  name: string;
  path: string;
  size?: number;
  serverModified?: string;
}

interface ListResponse {
  entries: DropboxEntry[];
  cursor?: string | null;
  hasMore?: boolean;
}

interface ReadOnlyStatus {
  configured: boolean;
  authorized: boolean;
  connected: boolean;
  rootPath: string | null;
  rootExists?: boolean;
  accountEmail?: string;
  lastRefresh?: string;
  error?: string;
}

interface DropboxBrowserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Files above this size download directly from Dropbox via a
 * just-in-time temporary link (fetched at click time, then immediate
 * redirect) — the app server never proxies the bytes. Smaller files use
 * the authenticated proxied route.
 */
const DIRECT_DOWNLOAD_THRESHOLD = 100 * 1024 * 1024; // 100 MB

function formatBytes(bytes?: number): string {
  if (bytes === undefined || bytes === null) return "";
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(value >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatDate(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString();
}

/** Build the display crumbs from the root path down to the current path. */
function buildBreadcrumbs(rootPath: string, currentPath: string) {
  const rootName = rootPath.split("/").filter(Boolean).pop() || rootPath;
  const crumbs: { name: string; path: string }[] = [{ name: rootName, path: rootPath }];
  if (currentPath && currentPath !== rootPath && currentPath.startsWith(rootPath)) {
    const rest = currentPath.slice(rootPath.length).split("/").filter(Boolean);
    let accum = rootPath;
    for (const part of rest) {
      accum = `${accum}/${part}`;
      crumbs.push({ name: part, path: accum });
    }
  }
  return crumbs;
}

export function DropboxBrowserDialog({ open, onOpenChange }: DropboxBrowserDialogProps) {
  const [currentPath, setCurrentPath] = useState<string>("");
  const [entries, setEntries] = useState<DropboxEntry[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const [downloadingPath, setDownloadingPath] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  /** Large files: fetch a fresh direct link at click time, then redirect. */
  const handleDirectDownload = async (entry: DropboxEntry) => {
    setDownloadingPath(entry.path);
    setDownloadError(null);
    try {
      const res = await authedFetch(
        `/api/dropbox-readonly/download-link?path=${encodeURIComponent(entry.path)}`,
      );
      if (!res.ok) {
        let message = `Request failed (${res.status})`;
        try {
          const body = await res.json();
          if (body?.error) message = body.error;
        } catch {
          /* ignore */
        }
        throw new Error(message);
      }
      const data: { link: string } = await res.json();
      // Immediate redirect — links are short-lived and must never be stored.
      window.location.assign(data.link);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : "Download failed");
    } finally {
      setDownloadingPath(null);
    }
  };

  // Fetch status once (for the configured root path).
  const { data: status, isLoading: statusLoading } = useQuery<ReadOnlyStatus>({
    queryKey: ["/api/dropbox-readonly/status"],
    enabled: open,
  });

  const rootPath = status?.rootPath || "";

  // Reset to root each time the dialog opens or the root changes.
  useEffect(() => {
    if (open && rootPath) {
      setCurrentPath(rootPath);
    }
  }, [open, rootPath]);

  // Load the first page for the current path.
  const {
    data: listData,
    isLoading: listLoading,
    isError: listIsError,
    error: listError,
  } = useQuery<ListResponse>({
    queryKey: ["/api/dropbox-readonly/list", currentPath],
    enabled: open && !!currentPath,
    queryFn: async () => {
      const res = await authedFetch(
        `/api/dropbox-readonly/list?path=${encodeURIComponent(currentPath)}`,
      );
      if (!res.ok) {
        let message = `Request failed (${res.status})`;
        try {
          const body = await res.json();
          if (body?.error) message = body.error;
        } catch {
          /* ignore */
        }
        throw new Error(message);
      }
      return res.json();
    },
  });

  // Sync the first-page result into the accumulating entries list.
  useEffect(() => {
    if (listData) {
      setEntries(listData.entries || []);
      setCursor(listData.cursor || null);
      setHasMore(!!listData.hasMore);
      setLoadMoreError(null);
    }
  }, [listData]);

  const handleLoadMore = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    setLoadMoreError(null);
    try {
      const res = await authedFetch(
        `/api/dropbox-readonly/list?path=${encodeURIComponent(currentPath)}&cursor=${encodeURIComponent(cursor)}`,
      );
      if (!res.ok) {
        let message = `Request failed (${res.status})`;
        try {
          const body = await res.json();
          if (body?.error) message = body.error;
        } catch {
          /* ignore */
        }
        throw new Error(message);
      }
      const data: ListResponse = await res.json();
      setEntries((prev) => [...prev, ...(data.entries || [])]);
      setCursor(data.cursor || null);
      setHasMore(!!data.hasMore);
    } catch (err) {
      setLoadMoreError(err instanceof Error ? err.message : "Failed to load more items");
    } finally {
      setLoadingMore(false);
    }
  };

  const navigateTo = (path: string) => {
    setCurrentPath(path);
    setEntries([]);
    setCursor(null);
    setHasMore(false);
    setLoadMoreError(null);
  };

  const breadcrumbs = rootPath ? buildBreadcrumbs(rootPath, currentPath) : [];

  const folders = entries.filter((e) => e.type === "folder");
  const files = entries.filter((e) => e.type === "file");
  const sortedEntries = [...folders, ...files];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl navy-bg border-gray-600 text-white"
        data-testid="dialog-dropbox-browser"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <FolderOpen className="h-5 w-5 text-blue-400" />
            Browse Read-Only Dropbox
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Browse and download files inside the configured folder. Navigation is
            limited to this folder and its subfolders.
          </DialogDescription>
        </DialogHeader>

        {statusLoading ? (
          <div className="flex items-center justify-center py-10 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Loading…
          </div>
        ) : !rootPath ? (
          <Alert className="bg-yellow-900/20 border-yellow-600">
            <AlertTriangle className="h-4 w-4 text-yellow-400" />
            <AlertDescription className="text-yellow-300">
              No root folder is configured for the read-only connection.
            </AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-3">
            {/* Breadcrumbs */}
            <div
              className="flex flex-wrap items-center gap-1 text-sm text-gray-300 p-2 bg-gray-800/50 border border-gray-700 rounded"
              data-testid="breadcrumbs-dropbox"
            >
              {breadcrumbs.map((crumb, idx) => (
                <span key={crumb.path} className="flex items-center gap-1">
                  {idx > 0 && <ChevronRight className="h-3 w-3 text-gray-500" />}
                  <button
                    type="button"
                    onClick={() => navigateTo(crumb.path)}
                    disabled={idx === breadcrumbs.length - 1}
                    className={
                      idx === breadcrumbs.length - 1
                        ? "text-white font-medium cursor-default"
                        : "text-blue-400 hover:text-blue-300 hover:underline"
                    }
                    data-testid={`breadcrumb-${idx}`}
                  >
                    {crumb.name}
                  </button>
                </span>
              ))}
            </div>

            {/* Listing */}
            <div className="border border-gray-700 rounded-lg max-h-[50vh] overflow-y-auto">
              {listLoading ? (
                <div className="flex items-center justify-center py-10 text-gray-400">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Loading folder…
                </div>
              ) : listIsError ? (
                <div className="p-4">
                  <Alert className="bg-red-900/20 border-red-600">
                    <AlertTriangle className="h-4 w-4 text-red-400" />
                    <AlertDescription className="text-red-300">
                      {listError instanceof Error
                        ? listError.message
                        : "Failed to load this folder."}
                    </AlertDescription>
                  </Alert>
                </div>
              ) : sortedEntries.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                  <Folder className="h-8 w-8 mb-2 text-gray-500" />
                  <span className="text-sm">This folder is empty.</span>
                </div>
              ) : (
                <ul className="divide-y divide-gray-700">
                  {sortedEntries.map((entry) => (
                    <li
                      key={entry.path}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-gray-700/40"
                      data-testid={`entry-${entry.type}`}
                    >
                      {entry.type === "folder" ? (
                        <button
                          type="button"
                          onClick={() => navigateTo(entry.path)}
                          className="flex items-center gap-3 flex-1 text-left min-w-0"
                          data-testid="button-open-folder"
                        >
                          <Folder className="h-5 w-5 text-blue-400 shrink-0" />
                          <span className="text-sm text-gray-200 truncate">{entry.name}</span>
                        </button>
                      ) : (
                        <>
                          <FileIcon className="h-5 w-5 text-gray-400 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-gray-200 truncate">{entry.name}</div>
                            <div className="text-xs text-gray-500 flex gap-3">
                              {entry.size !== undefined && (
                                <span>{formatBytes(entry.size)}</span>
                              )}
                              {entry.serverModified && (
                                <span>{formatDate(entry.serverModified)}</span>
                              )}
                            </div>
                          </div>
                          {entry.size !== undefined && entry.size > DIRECT_DOWNLOAD_THRESHOLD ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-gray-400 hover:text-white shrink-0"
                              onClick={() => handleDirectDownload(entry)}
                              disabled={downloadingPath === entry.path}
                              data-testid="button-download-file-direct"
                              title="Download directly from Dropbox (large file)"
                            >
                              {downloadingPath === entry.path ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Download className="h-4 w-4" />
                              )}
                            </Button>
                          ) : (
                            <a
                              href={`/api/dropbox-readonly/download?path=${encodeURIComponent(entry.path)}`}
                              download
                              className="shrink-0"
                              data-testid="button-download-file"
                              title="Download"
                            >
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-gray-400 hover:text-white"
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            </a>
                          )}
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Download error */}
            {downloadError && (
              <Alert className="bg-red-900/20 border-red-600" data-testid="alert-download-error">
                <AlertTriangle className="h-4 w-4 text-red-400" />
                <AlertDescription className="text-red-300">{downloadError}</AlertDescription>
              </Alert>
            )}

            {/* Load more */}
            {loadMoreError && (
              <Alert className="bg-red-900/20 border-red-600">
                <AlertTriangle className="h-4 w-4 text-red-400" />
                <AlertDescription className="text-red-300">{loadMoreError}</AlertDescription>
              </Alert>
            )}
            {hasMore && !listLoading && !listIsError && (
              <div className="flex justify-center">
                <Button
                  variant="outline"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                  data-testid="button-load-more"
                >
                  {loadingMore ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Loading…
                    </>
                  ) : (
                    "Load more"
                  )}
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
